// Stable prefix constants for every warn-level breadcrumb the session
// module emits. These are the strings that end up in Sentry and that
// chaos/stability tests match on — they must not drift silently.
//
// Rules:
// - The constant holds the STABLE PREFIX only. Interpolated detail
//   (attempt numbers, device ids, error messages) lives at the call
//   site. Tests assert on the prefix via String#includes.
// - A mirror lives at tests/playwright/session-breadcrumbs.js. Keep the
//   two files aligned — the Playwright suite can't import TS because
//   tsconfig doesn't enable allowJs/isolatedModules for that boundary.
// - Before renaming an entry, grep for its string value across this
//   repo AND the monitoring/alerts side — Sentry dashboards key on the
//   literal text.

export const SessionBreadcrumb = {
  // StreamSessionMedia — callingState$ transitions
  STREAM_RECONNECTING: 'stream: reconnecting',
  STREAM_RECONNECT_FAILED: 'stream: failed to reconnect',
  STREAM_GO_AWAY: 'stream: goAway',

  // StreamSessionMedia — connection.error classification
  STREAM_TRANSIENT_ERROR: 'stream: transient connection error',
  STREAM_REDUCING_QUALITY: 'stream: reducing publish quality due to sustained poor connection',

  // StreamSessionMedia — track-ended handlers
  STREAM_CAMERA_TRACK_ENDED: 'stream: local camera track ended unexpectedly',
  STREAM_MIC_TRACK_ENDED: 'stream: local microphone track ended unexpectedly',

  // StreamSessionMedia — republish scheduler
  STREAM_CAMERA_REPUBLISH_SCHEDULED: 'stream: scheduling camera republish',
  STREAM_MIC_REPUBLISH_SCHEDULED: 'stream: scheduling mic republish',
  STREAM_CAMERA_REPUBLISH_FAILED: 'stream: camera republish attempt',
  STREAM_MIC_REPUBLISH_FAILED: 'stream: mic republish attempt',

  // StreamSessionMedia — post-reconnect verification
  STREAM_RECONNECT_CAMERA_REENABLE_FAILED: 'stream: failed to re-enable camera after reconnection',
  STREAM_RECONNECT_MIC_REENABLE_FAILED: 'stream: failed to re-enable microphone after reconnection',

  // StreamSessionMedia — outer reconnection recovery loop
  STREAM_RECONNECT_ATTEMPT: 'stream: reconnection attempt',

  // StreamSessionMedia — device disconnect fallback
  STREAM_CAMERA_DISCONNECTED: 'stream: active camera disconnected',
  STREAM_MIC_DISCONNECTED: 'stream: active microphone disconnected',
  STREAM_SPEAKER_DISCONNECTED: 'stream: active speaker disconnected',
  STREAM_NO_CAMERAS_LEFT: 'stream: no cameras available after device change',
  STREAM_NO_MICS_LEFT: 'stream: no microphones available after device change',

  // StreamSessionMedia — adaptive fallback
  STREAM_AUDIO_ONLY_FALLBACK: 'stream: entering audio-only mode after repeated video failures',

  // SessionLifecycleGuardian
  LIFECYCLE_OFFLINE: 'SessionLifecycleGuardian: offline',
  LIFECYCLE_BFCACHE_RESTORE: 'SessionLifecycleGuardian: BFCache restore',
  LIFECYCLE_CAMERA_GONE: 'SessionLifecycleGuardian: current camera',
  LIFECYCLE_MIC_GONE: 'SessionLifecycleGuardian: current microphone',
  LIFECYCLE_CAMERA_PERMISSION: 'SessionLifecycleGuardian: camera permission changed to',
  LIFECYCLE_MIC_PERMISSION: 'SessionLifecycleGuardian: microphone permission changed to',

  // MediaWatchdog
  WATCHDOG_VIDEO_STALL: 'MediaWatchdog: video stall detected',
  WATCHDOG_VIDEO_NOT_ALIVE: 'MediaWatchdog: source video track sustained not-alive',
  WATCHDOG_AUDIO_NOT_ALIVE: 'MediaWatchdog: audio reported not-alive',
} as const;

export type SessionBreadcrumbKey = keyof typeof SessionBreadcrumb;
